import React, { useState, useEffect } from "react";
import {
    makeStyles,
    Paper,
    Grid,
    TextField,
    Table,
    TableHead,
    TableBody,
    TableCell,
    TableRow,
    IconButton,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Typography,
    Box,
    useTheme
} from "@material-ui/core";
import { Formik, Form, Field } from 'formik';
import ButtonWithSpinner from "../ButtonWithSpinner";
import ConfirmationModal from "../ConfirmationModal";

import { Edit as EditIcon, Assignment as PlanIcon } from "@material-ui/icons";

import { toast } from "react-toastify";
import usePlans from "../../hooks/usePlans";
import { i18n } from "../../translate/i18n";


const useStyles = makeStyles(theme => ({
    root: {
        width: '100%',
        padding: theme.spacing(1),
    },
    mainPaper: {
        width: '100%',
        flex: 1,
        background: "transparent",
        overflowX: "hidden",
    },
    fullWidth: {
        width: '100%'
    },
    formCard: {
        background: theme.mode === "dark"
            ? "linear-gradient(145deg, rgba(48,48,68,0.6), rgba(36,36,53,0.8))"
            : "linear-gradient(145deg, #ffffff, #f8f9fa)",
        borderRadius: 5,
        padding: theme.spacing(3),
        marginBottom: theme.spacing(3),
        border: theme.mode === "dark" ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.06)",
        boxShadow: theme.mode === "dark"
            ? "0 8px 24px rgba(0,0,0,0.4)"
            : "0 4px 20px rgba(0,0,0,0.08)",
    },
    formTitle: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1.5),
        marginBottom: theme.spacing(3),
        "& svg": {
            fontSize: 28,
            color: theme.mode === "dark" ? "#c4b5fd" : "#8b5cf6",
        },
        "& h6": {
            fontWeight: 700,
            fontSize: "1.25rem",
            color: theme.mode === "dark" ? "#ffffff" : "transparent",
            background: theme.mode === "dark" ? "transparent" : "linear-gradient(135deg, #8b5cf6, #a78bfa)",
            WebkitBackgroundClip: theme.mode === "dark" ? "unset" : "text",
            WebkitTextFillColor: theme.mode === "dark" ? "#ffffff" : "transparent",
            textShadow: theme.mode === "dark" ? "0 2px 8px rgba(139,92,246,0.5)" : "none",
            letterSpacing: "0.5px",
        },
    },
    styledTextField: {
        "& .MuiOutlinedInput-root": {
            borderRadius: 5,
            background: theme.mode === "dark"
                ? "rgba(255,255,255,0.03)"
                : "rgba(255,255,255,0.8)",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
                background: theme.mode === "dark"
                    ? "rgba(255,255,255,0.05)"
                    : "rgba(255,255,255,1)",
                transform: "translateY(-1px)",
            },
            "&.Mui-focused": {
                background: theme.mode === "dark"
                    ? "rgba(139,92,246,0.08)"
                    : "rgba(139,92,246,0.04)",
                boxShadow: "0 0 0 3px rgba(139,92,246,0.1)",
            },
        },
        "& .MuiInputLabel-root": {
            color: theme.mode === "dark" ? "#c4b5fd" : "#8b5cf6",
            fontWeight: 500,
        },
    },
    styledSelect: {
        borderRadius: 5,
        background: theme.mode === "dark"
            ? "rgba(255,255,255,0.03)"
            : "rgba(255,255,255,0.8)",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
            background: theme.mode === "dark"
                ? "rgba(255,255,255,0.05)"
                : "rgba(255,255,255,1)",
        },
        "&.Mui-focused": {
            background: theme.mode === "dark"
                ? "rgba(139,92,246,0.08)"
                : "rgba(139,92,246,0.04)",
        },
    },
    buttonGroup: {
        display: "flex",
        gap: theme.spacing(1),
        justifyContent: "flex-end",
        marginTop: theme.spacing(2),
    },
    actionButton: {
        borderRadius: 5,
        textTransform: "none",
        fontWeight: 600,
        padding: "8px 20px",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: theme.mode === "dark"
                ? "0 6px 16px rgba(139,92,246,0.4)"
                : "0 6px 16px rgba(139,92,246,0.3)",
        },
    },
    tableContainer: {
        background: theme.mode === "dark"
            ? "linear-gradient(145deg, rgba(48,48,68,0.4), rgba(36,36,53,0.6))"
            : "linear-gradient(145deg, #ffffff, #f8f9fa)",
        borderRadius: 5,
        border: theme.mode === "dark" ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.06)",
        boxShadow: theme.mode === "dark"
            ? "0 8px 24px rgba(0,0,0,0.4)"
            : "0 4px 20px rgba(0,0,0,0.08)",
        overflow: "hidden",
        overflowX: "auto",
        ...theme.scrollbarStyles,
    },
    tableHeader: {
        background: theme.mode === "dark"
            ? "linear-gradient(135deg, rgba(139,92,246,0.15), rgba(167,139,250,0.15))"
            : "linear-gradient(135deg, rgba(139,92,246,0.08), rgba(167,139,250,0.08))",
        "& .MuiTableCell-head": {
            color: theme.mode === "dark" ? "#c4b5fd" : "#8b5cf6",
            fontWeight: 700,
            fontSize: "0.875rem",
            letterSpacing: "0.5px",
            textTransform: "uppercase",
            padding: theme.spacing(2),
            borderBottom: theme.mode === "dark"
                ? "2px solid rgba(139,92,246,0.3)"
                : "2px solid rgba(139,92,246,0.2)",
        },
    },
    tableRow: {
        transition: "all 0.2s ease",
        "&:hover": {
            background: theme.mode === "dark"
                ? "rgba(139,92,246,0.08)"
                : "rgba(139,92,246,0.04)",
            transform: "scale(1.001)",
        },
        "&:nth-of-type(even)": {
            background: theme.mode === "dark"
                ? "rgba(255,255,255,0.02)"
                : "rgba(0,0,0,0.02)",
        },
    },
    tableCell: {
        padding: theme.spacing(1.5),
        borderBottom: theme.mode === "dark"
            ? "1px solid rgba(255,255,255,0.05)"
            : "1px solid rgba(0,0,0,0.05)",
    },
    editButton: {
        color: theme.mode === "dark" ? "#c4b5fd" : "#8b5cf6",
        padding: 8,
        transition: "all 0.3s ease",
        "&:hover": {
            background: theme.mode === "dark"
                ? "rgba(139,92,246,0.15)"
                : "rgba(139,92,246,0.1)",
            transform: "rotate(15deg) scale(1.1)",
        },
    },
}));

export function PlanManagerForm(props) {
    const { onSubmit, onDelete, onCancel, initialValue, loading } = props;
    const classes = useStyles();
    const theme = useTheme();
    const isDark = theme.mode === "dark";

    const [record, setRecord] = useState({
        name: '',
        users: 0,
        connections: 0,
        queues: 0,
        amount: 0,
        useWhatsapp: true,
        useFacebook: true,
        useInstagram: true,
        useCampaigns: true,
        useSchedules: true,
        useInternalChat: true,
        useExternalApi: true,
        useKanban: true,
        useOpenAi: true,
        useIntegrations: true,
        isPublic: true,
        useWhatsappOfficial: false,
        trial: false,
        trialDays: 3,
        recurrence: 'monthly',
        wavoip: false
    });

    useEffect(() => {
        setRecord(initialValue)
    }, [initialValue])

    const handleSubmit = async (data) => {
        onSubmit(data)
    }

    return (
        <Box className={classes.formCard}>
            <Box className={classes.formTitle}>
                <PlanIcon />
                <Typography variant="h6">
                    {record.id ? "Editar Plano" : "Cadastrar Novo Plano"}
                </Typography>
            </Box>

            <Formik
                enableReinitialize
                className={classes.fullWidth}
                initialValues={record}
                onSubmit={(values, { resetForm }) =>
                    setTimeout(() => {
                        handleSubmit(values)
                        resetForm()
                    }, 500)
                }
            >
                {(values) => (
                    <Form className={classes.fullWidth}>
                        <Grid spacing={2} justifyContent="flex-start" container>

                            {/* PUBLICO */}
                            <Grid xs={12} sm={6} md={1} xl={1} item>
                                <FormControl margin="dense" variant="outlined" fullWidth className={classes.styledTextField}>
                                    <InputLabel htmlFor="status-selection">{i18n.t("plans.form.public")}</InputLabel>
                                    <Field
                                        as={Select}
                                        id="status-selection"
                                        label={i18n.t("plans.form.public")}
                                        labelId="status-selection-label"
                                        name="isPublic"
                                        margin="dense"
                                        className={classes.styledSelect}
                                    >
                                        <MenuItem value={true}>Sim</MenuItem>
                                        <MenuItem value={false}>Não</MenuItem>
                                    </Field>
                                </FormControl>
                            </Grid>

                            {/* TRIAL */}
                            <Grid xs={12} sm={6} md={1} xl={1} item>
                                <FormControl margin="dense" variant="outlined" fullWidth className={classes.styledTextField}>
                                    <InputLabel htmlFor="trial-selection">Trial</InputLabel>
                                    <Field
                                        as={Select}
                                        id="trial-selection"
                                        label="Trial"
                                        labelId="trial-selection-label"
                                        name="trial"
                                        margin="dense"
                                        className={classes.styledSelect}
                                    >
                                        <MenuItem value={true}>Sim</MenuItem>
                                        <MenuItem value={false}>Não</MenuItem>
                                    </Field>
                                </FormControl>
                            </Grid>

                            {/* TRIAL DAYS */}
                            <Grid xs={12} sm={6} md={1} xl={1} item>
                                <Field
                                    as={TextField}
                                    label={i18n.t("plans.form.trialDays")}
                                    name="trialDays"
                                    variant="outlined"
                                    className={`${classes.fullWidth} ${classes.styledTextField}`}
                                    margin="dense"
                                    type="number"
                                    inputProps={{ min: 1 }}
                                    disabled={!values.values.trial}
                                />
                            </Grid>

                            {/* NOME */}
                            <Grid xs={12} sm={6} md={3} xl={3} item>
                                <Field
                                    as={TextField}
                                    label={i18n.t("plans.form.name")}
                                    name="name"
                                    variant="outlined"
                                    className={`${classes.fullWidth} ${classes.styledTextField}`}
                                    margin="dense"
                                />
                            </Grid>

                            {/* USUARIOS */}
                            <Grid xs={12} sm={6} md={1} item>
                                <Field
                                    as={TextField}
                                    label={i18n.t("plans.form.users")}
                                    name="users"
                                    variant="outlined"
                                    className={`${classes.fullWidth} ${classes.styledTextField}`}
                                    margin="dense"
                                    type="number"
                                />
                            </Grid>

                            {/* CONEXOES */}
                            <Grid xs={12} sm={6} md={1} item>
                                <Field
                                    as={TextField}
                                    label={i18n.t("plans.form.connections")}
                                    name="connections"
                                    variant="outlined"
                                    className={`${classes.fullWidth} ${classes.styledTextField}`}
                                    margin="dense"
                                    type="number"
                                />
                            </Grid>

                            {/* FILAS */}
                            <Grid xs={12} sm={6} md={1} item>
                                <Field
                                    as={TextField}
                                    label="Filas"
                                    name="queues"
                                    variant="outlined"
                                    className={`${classes.fullWidth} ${classes.styledTextField}`}
                                    margin="dense"
                                    type="number"
                                />
                            </Grid>

                            {/* VALOR */}
                            <Grid xs={12} sm={6} md={1} item>
                                <Field
                                    as={TextField}
                                    label="Valor"
                                    name="amount"
                                    variant="outlined"
                                    className={`${classes.fullWidth} ${classes.styledTextField}`}
                                    margin="dense"
                                    type="text"
                                />
                            </Grid>

                            {/* WHATSAPP */}
                            <Grid xs={12} sm={6} md={2} item>
                                <FormControl margin="dense" variant="outlined" fullWidth className={classes.styledTextField}>
                                    <InputLabel htmlFor="useWhatsapp-selection">Whatsapp</InputLabel>
                                    <Field
                                        as={Select}
                                        id="useWhatsapp-selection"
                                        label="Whatsapp"
                                        labelId="useWhatsapp-selection-label"
                                        name="useWhatsapp"
                                        margin="dense"
                                        className={classes.styledSelect}
                                    >
                                        <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                        <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                    </Field>
                                </FormControl>
                            </Grid>

                            {/* WHATSAPP OFICIAL */}
                            <Grid xs={12} sm={6} md={2} item>
                                <FormControl margin="dense" variant="outlined" fullWidth className={classes.styledTextField}>
                                    <InputLabel htmlFor="useWhatsappOfficial-selection">Whatsapp Oficial</InputLabel>
                                    <Field
                                        as={Select}
                                        id="useWhatsappOfficial-selection"
                                        label="Whatsapp Oficial"
                                        labelId="useWhatsappOfficial-selection-label"
                                        name="useWhatsappOfficial"
                                        margin="dense"
                                        className={classes.styledSelect}
                                    >
                                        <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                        <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                    </Field>
                                </FormControl>
                            </Grid>

                            {/* FACEBOOK */}
                            <Grid xs={12} sm={6} md={2} item>
                                <FormControl margin="dense" variant="outlined" fullWidth className={classes.styledTextField}>
                                    <InputLabel htmlFor="useFacebook-selection">Facebook</InputLabel>
                                    <Field
                                        as={Select}
                                        id="useFacebook-selection"
                                        label="Facebook"
                                        labelId="useFacebook-selection-label"
                                        name="useFacebook"
                                        margin="dense"
                                        className={classes.styledSelect}
                                    >
                                        <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                        <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                    </Field>
                                </FormControl>
                            </Grid>

                            {/* INSTAGRAM */}
                            <Grid xs={12} sm={6} md={2} item>
                                <FormControl margin="dense" variant="outlined" fullWidth className={classes.styledTextField}>
                                    <InputLabel htmlFor="useInstagram-selection">Instagram</InputLabel>
                                    <Field
                                        as={Select}
                                        id="useInstagram-selection"
                                        label="Instagram"
                                        labelId="useInstagram-selection-label"
                                        name="useInstagram"
                                        margin="dense"
                                        className={classes.styledSelect}
                                    >
                                        <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                        <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                    </Field>
                                </FormControl>
                            </Grid>

                            {/* CAMPANHAS */}
                            <Grid xs={12} sm={6} md={2} item>
                                <FormControl margin="dense" variant="outlined" fullWidth className={classes.styledTextField}>
                                    <InputLabel htmlFor="useCampaigns-selection">{i18n.t("plans.form.campaigns")}</InputLabel>
                                    <Field
                                        as={Select}
                                        id="useCampaigns-selection"
                                        label={i18n.t("plans.form.campaigns")}
                                        labelId="useCampaigns-selection-label"
                                        name="useCampaigns"
                                        margin="dense"
                                        className={classes.styledSelect}
                                    >
                                        <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                        <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                    </Field>
                                </FormControl>
                            </Grid>

                            {/* AGENDAMENTOS */}
                            <Grid xs={12} sm={8} md={2} item>
                                <FormControl margin="dense" variant="outlined" fullWidth className={classes.styledTextField}>
                                    <InputLabel htmlFor="useSchedules-selection">{i18n.t("plans.form.schedules")}</InputLabel>
                                    <Field
                                        as={Select}
                                        id="useSchedules-selection"
                                        label={i18n.t("plans.form.schedules")}
                                        labelId="useSchedules-selection-label"
                                        name="useSchedules"
                                        margin="dense"
                                        className={classes.styledSelect}
                                    >
                                        <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                        <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                    </Field>
                                </FormControl>
                            </Grid>

                            {/* CHAT INTERNO */}
                            <Grid xs={12} sm={8} md={2} item>
                                <FormControl margin="dense" variant="outlined" fullWidth className={classes.styledTextField}>
                                    <InputLabel htmlFor="useInternalChat-selection">Chat Interno</InputLabel>
                                    <Field
                                        as={Select}
                                        id="useInternalChat-selection"
                                        label="Chat Interno"
                                        labelId="useInternalChat-selection-label"
                                        name="useInternalChat"
                                        margin="dense"
                                        className={classes.styledSelect}
                                    >
                                        <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                        <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                    </Field>
                                </FormControl>
                            </Grid>

                            {/* API Externa */}
                            <Grid xs={12} sm={8} md={2} item>
                                <FormControl margin="dense" variant="outlined" fullWidth className={classes.styledTextField}>
                                    <InputLabel htmlFor="useExternalApi-selection">API Externa</InputLabel>
                                    <Field
                                        as={Select}
                                        id="useExternalApi-selection"
                                        label="API Externa"
                                        labelId="useExternalApi-selection-label"
                                        name="useExternalApi"
                                        margin="dense"
                                        className={classes.styledSelect}
                                    >
                                        <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                        <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                    </Field>
                                </FormControl>
                            </Grid>

                            {/* KANBAN */}
                            <Grid xs={12} sm={8} md={2} item>
                                <FormControl margin="dense" variant="outlined" fullWidth className={classes.styledTextField}>
                                    <InputLabel htmlFor="useKanban-selection">Kanban</InputLabel>
                                    <Field
                                        as={Select}
                                        id="useKanban-selection"
                                        label="Kanban"
                                        labelId="useKanban-selection-label"
                                        name="useKanban"
                                        margin="dense"
                                        className={classes.styledSelect}
                                    >
                                        <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                        <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                    </Field>
                                </FormControl>
                            </Grid>

                            {/* OPENAI */}
                            <Grid xs={12} sm={8} md={2} item>
                                <FormControl margin="dense" variant="outlined" fullWidth className={classes.styledTextField}>
                                    <InputLabel htmlFor="useOpenAi-selection">Talk.Ai</InputLabel>
                                    <Field
                                        as={Select}
                                        id="useOpenAi-selection"
                                        label="Talk.Ai"
                                        labelId="useOpenAi-selection-label"
                                        name="useOpenAi"
                                        margin="dense"
                                        className={classes.styledSelect}
                                    >
                                        <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                        <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                    </Field>
                                </FormControl>
                            </Grid>

                            {/* INTEGRACOES */}
                            <Grid xs={12} sm={8} md={2} item>
                                <FormControl margin="dense" variant="outlined" fullWidth className={classes.styledTextField}>
                                    <InputLabel htmlFor="useIntegrations-selection">Integrações</InputLabel>
                                    <Field
                                        as={Select}
                                        id="useIntegrations-selection"
                                        label="Integrações"
                                        labelId="useIntegrations-selection-label"
                                        name="useIntegrations"
                                        margin="dense"
                                        className={classes.styledSelect}
                                    >
                                        <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                        <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                    </Field>
                                </FormControl>
                            </Grid>

                            {/* WAVOIP */}
                            <Grid xs={12} sm={8} md={2} item>
                                <FormControl margin="dense" variant="outlined" fullWidth className={classes.styledTextField}>
                                    <InputLabel htmlFor="wavoip-selection">Wavoip</InputLabel>
                                    <Field
                                        as={Select}
                                        id="wavoip-selection"
                                        label="Wavoip"
                                        labelId="wavoip-selection-label"
                                        name="wavoip"
                                        margin="dense"
                                        className={classes.styledSelect}
                                    >
                                        <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                        <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                    </Field>
                                </FormControl>
                            </Grid>

                        </Grid>
                        <Box className={classes.buttonGroup}>
                            <ButtonWithSpinner className={classes.actionButton} loading={loading} onClick={() => onCancel()} variant="contained">
                                {i18n.t("plans.form.clear")}
                            </ButtonWithSpinner>
                            {record.id !== undefined ? (
                                <ButtonWithSpinner className={classes.actionButton} loading={loading} onClick={() => onDelete(record)} variant="contained" color="secondary">
                                    {i18n.t("plans.form.delete")}
                                </ButtonWithSpinner>
                            ) : null}
                            <ButtonWithSpinner className={classes.actionButton} loading={loading} type="submit" variant="contained" color="primary">
                                {i18n.t("plans.form.save")}
                            </ButtonWithSpinner>
                        </Box>
                    </Form>
                )}
            </Formik>
        </Box>
    )
}

export function PlansManagerGrid(props) {
    const { records, onSelect } = props;
    const classes = useStyles();
    const theme = useTheme();
    const isDark = theme.mode === "dark";
    const renderWhatsapp = (row) => {
        return row.useWhatsapp === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };

    const renderWhatsappOfficial = (row) => {
        return row.useWhatsappOfficial === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };

    const renderFacebook = (row) => {
        return row.useFacebook === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };

    const renderInstagram = (row) => {
        return row.useInstagram === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };

    const renderCampaigns = (row) => {
        return row.useCampaigns === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };

    const renderSchedules = (row) => {
        return row.useSchedules === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };

    const renderInternalChat = (row) => {
        return row.useInternalChat === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };

    const renderExternalApi = (row) => {
        return row.useExternalApi === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };

    const renderKanban = (row) => {
        return row.useKanban === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };

    const renderOpenAi = (row) => {
        return row.useOpenAi === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };

    const renderIntegrations = (row) => {
        return row.useIntegrations === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };

    const renderWavoip = (row) => {
        return row.wavoip === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };

    return (
        <Paper className={classes.tableContainer} elevation={0}>
            <Table
                className={classes.fullWidth}
                padding="normal"
                aria-label="plans table"
            >
                <TableHead className={classes.tableHeader}>
                    <TableRow>
                        <TableCell align="center" style={{ width: '1%' }}>#</TableCell>
                        <TableCell align="center">{i18n.t("plans.form.trial")}</TableCell>
                        <TableCell align="left">{i18n.t("plans.form.name")}</TableCell>
                        <TableCell align="center">{i18n.t("plans.form.users")}</TableCell>
                        <TableCell align="center">{i18n.t("plans.form.public")}</TableCell>
                        <TableCell align="center">{i18n.t("plans.form.connections")}</TableCell>
                        <TableCell align="center">Filas</TableCell>
                        <TableCell align="center">Valor</TableCell>
                        <TableCell align="center">Whatsapp</TableCell>
                        <TableCell align="center">Whatsapp Oficial</TableCell>
                        <TableCell align="center">Facebook</TableCell>
                        <TableCell align="center">Instagram</TableCell>
                        <TableCell align="center">{i18n.t("plans.form.campaigns")}</TableCell>
                        <TableCell align="center">{i18n.t("plans.form.schedules")}</TableCell>
                        <TableCell align="center">Chat Interno</TableCell>
                        <TableCell align="center">API Externa</TableCell>
                        <TableCell align="center">Kanban</TableCell>
                        <TableCell align="center">Talk.Ai</TableCell>
                        <TableCell align="center">Integrações</TableCell>
                        <TableCell align="center">Wavoip</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {records.map((row) => (
                        <TableRow className={classes.tableRow} key={row.id}>
                            <TableCell className={classes.tableCell} align="center" style={{ width: '1%' }}>
                                <IconButton className={classes.editButton} onClick={() => onSelect(row)} aria-label="edit" size="small">
                                    <EditIcon />
                                </IconButton>
                            </TableCell>
                            <TableCell className={classes.tableCell} align="center">{row.trialDays || '-'}</TableCell>
                            <TableCell className={classes.tableCell} align="left">{row.name || '-'}</TableCell>
                            <TableCell className={classes.tableCell} align="center">{row.users || '-'}</TableCell>
                            <TableCell className={classes.tableCell} align="center">{row.isPublic ? "Sim" : "Não" || '-'}</TableCell>
                            <TableCell className={classes.tableCell} align="center">{row.connections || '-'}</TableCell>
                            <TableCell className={classes.tableCell} align="center">{row.queues || '-'}</TableCell>
                            <TableCell className={classes.tableCell} align="center">{i18n.t("plans.form.money")} {row.amount ? row.amount.toLocaleString('pt-br', { minimumFractionDigits: 2 }) : '00.00'}</TableCell>
                            <TableCell className={classes.tableCell} align="center">{renderWhatsapp(row)}</TableCell>
                            <TableCell className={classes.tableCell} align="center">{renderWhatsappOfficial(row)}</TableCell>
                            <TableCell className={classes.tableCell} align="center">{renderFacebook(row)}</TableCell>
                            <TableCell className={classes.tableCell} align="center">{renderInstagram(row)}</TableCell>
                            <TableCell className={classes.tableCell} align="center">{renderCampaigns(row)}</TableCell>
                            <TableCell className={classes.tableCell} align="center">{renderSchedules(row)}</TableCell>
                            <TableCell className={classes.tableCell} align="center">{renderInternalChat(row)}</TableCell>
                            <TableCell className={classes.tableCell} align="center">{renderExternalApi(row)}</TableCell>
                            <TableCell className={classes.tableCell} align="center">{renderKanban(row)}</TableCell>
                            <TableCell className={classes.tableCell} align="center">{renderOpenAi(row)}</TableCell>
                            <TableCell className={classes.tableCell} align="center">{renderIntegrations(row)}</TableCell>
                            <TableCell className={classes.tableCell} align="center">{renderWavoip(row)}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Paper>
    )
}

export default function PlansManager() {
    const classes = useStyles()
    const { list, save, update, remove } = usePlans()

    const [showConfirmDialog, setShowConfirmDialog] = useState(false)
    const [loading, setLoading] = useState(false)
    const [records, setRecords] = useState([])
    const [record, setRecord] = useState({
        name: '',
        users: 0,
        connections: 0,
        queues: 0,
        amount: 0,
        useWhatsapp: true,
        useFacebook: true,
        useInstagram: true,
        useCampaigns: true,
        useSchedules: true,
        useInternalChat: true,
        useExternalApi: true,
        useKanban: true,
        useOpenAi: true,
        useIntegrations: true,
        isPublic: true,
        useWhatsappOfficial: false,
        trial: false,
        trialDays: 3,
        recurrence: 'monthly',
        wavoip: false
    })

    useEffect(() => {
        async function fetchData() {
            await loadPlans()
        }
        fetchData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [record])

    const loadPlans = async () => {
        setLoading(true)
        try {
            const planList = await list()
            setRecords(planList)
        } catch (e) {
            toast.error('Não foi possível carregar a lista de registros')
        }
        setLoading(false)
    }

    const handleSubmit = async (data) => {
        setLoading(true)
        console.log(data)
        try {
            if (data.id !== undefined) {
                await update(data)
            } else {
                await save(data)
            }
            await loadPlans()
            handleCancel()
            toast.success('Operação realizada com sucesso!')
        } catch (e) {
            toast.error('Não foi possível realizar a operação. Verifique se já existe uma plano com o mesmo nome ou se os campos foram preenchidos corretamente')
        }
        setLoading(false)
    }

    const handleDelete = async () => {
        setLoading(true)
        try {
            await remove(record.id)
            await loadPlans()
            handleCancel()
            toast.success('Operação realizada com sucesso!')
        } catch (e) {
            toast.error('Não foi possível realizar a operação')
        }
        setLoading(false)
    }

    const handleOpenDeleteDialog = () => {
        setShowConfirmDialog(true)
    }

    const handleCancel = () => {
        setRecord({
            id: undefined,
            name: '',
            users: 0,
            connections: 0,
            queues: 0,
            amount: 0,
            useWhatsapp: true,
            useFacebook: true,
            useInstagram: true,
            useCampaigns: true,
            useSchedules: true,
            useInternalChat: true,
            useExternalApi: true,
            useKanban: true,
            useOpenAi: true,
            useIntegrations: true,
            isPublic: true,
            useWhatsappOfficial: false,
            trial: false,
            trialDays: 3,
            recurrence: 'monthly',
            wavoip: false
        })
    }

    const handleSelect = (data) => {

        let useWhatsapp = data.useWhatsapp === false ? false : true
        let useWhatsappOfficial = data.useWhatsappOfficial === false ? false : true
        let useFacebook = data.useFacebook === false ? false : true
        let useInstagram = data.useInstagram === false ? false : true
        let useCampaigns = data.useCampaigns === false ? false : true
        let useSchedules = data.useSchedules === false ? false : true
        let useInternalChat = data.useInternalChat === false ? false : true
        let useExternalApi = data.useExternalApi === false ? false : true
        let useKanban = data.useKanban === false ? false : true
        let useOpenAi = data.useOpenAi === false ? false : true
        let useIntegrations = data.useIntegrations === false ? false : true
        let wavoip = data.wavoip === false ? false : true

        setRecord({
            id: data.id,
            name: data.name || '',
            users: data.users || 0,
            connections: data.connections || 0,
            queues: data.queues || 0,
            amount: data.amount?.toLocaleString('pt-br', { minimumFractionDigits: 2 }) || 0,
            useWhatsapp,
            useWhatsappOfficial,
            useFacebook,
            useInstagram,
            useCampaigns,
            useSchedules,
            useInternalChat,
            useExternalApi,
            useKanban,
            useOpenAi,
            useIntegrations,
            isPublic: data.isPublic,
            trial: data.trial,
            trialDays: data.trialDays,
            recurrence: data.recurrence,
            wavoip
        })
    }

    return (
        <Paper className={classes.mainPaper} elevation={0}>
            <Grid spacing={3} container>
                <Grid xs={12} item>
                    <PlanManagerForm
                        initialValue={record}
                        onDelete={handleOpenDeleteDialog}
                        onSubmit={handleSubmit}
                        onCancel={handleCancel}
                        loading={loading}
                    />
                </Grid>
                <Grid xs={12} item>
                    <PlansManagerGrid
                        records={records}
                        onSelect={handleSelect}
                    />
                </Grid>
            </Grid>
            <ConfirmationModal
                title="Exclusão de Registro"
                open={showConfirmDialog}
                onClose={() => setShowConfirmDialog(false)}
                onConfirm={() => handleDelete()}
            >
                Deseja realmente excluir esse registro?
            </ConfirmationModal>
        </Paper>
    )
}